/**
 * invoiceHtml.js — builds a self-contained, printable invoice document dressed
 * in the SAME STR palette + fonts as the published Reports and
 * Mockups (brass #A98953, obsidian #0A0A0C, bone #EFEBE4, Bodoni Moda + Inter Tight + JetBrains Mono).
 *
 * `openInvoiceDocument()` opens it in a new tab with a "Download PDF" button
 * (window.print, A4) — mirroring how Reports/Mockups produce their PDFs.
 */
import { fmtDate, fmtAmt } from './helpers';

const BANK = {
  name: 'SHAHBAZ MUNIR',
  bank: 'Meezan Bank – Peoples Colony, Faisalabad',
  account: '04030100161735',
  iban: 'PK35MEZN0004030100161735',
};

// International clients (billed in USD/GBP/AED) pay into the company's Bank Alfalah
// account, which supports SWIFT wire transfers.
const BANK_INTL = {
  title: 'STRATEGISTIS INFORMATIONS (SMC-PVT) LTD.',
  bank: 'Bank Alfalah Limited',
  branchCode: '5729',
  account: '5001988069',
  iban: 'PK91ALFH5729005001988069',
  swift: 'ALFHPKKA729',
};

const esc = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

export function buildInvoiceHtml(client = {}, invoice = {}) {
  const curr = invoice.currency || client.currency || 'PKR';
  const INK = '#0A0A0C', TXT = '#EFEBE4',
    LINE = 'rgba(239,235,228,0.12)', HAIR = 'rgba(239,235,228,0.09)',
    MUT = 'rgba(239,235,228,0.55)', FAINT = 'rgba(239,235,228,0.42)';

  const STATUS = {
    paid:      { label: 'PAID',      color: '#8FB08A' },
    pending:   { label: 'PENDING',   color: '#C79A5B' },
    sent:      { label: 'SENT',      color: '#C79A5B' },
    generated: { label: 'GENERATED', color: '#8FA3B0' },
    draft:     { label: 'DRAFT',     color: FAINT },
  };
  const st = STATUS[invoice.status] || STATUS.draft;

  // "6 AUG 2026" from a YYYY-MM-DD string
  const MON = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  const MONL = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const longDate = (ymd) => { const p = String(ymd || '').split('-'); if (p.length < 3) return '—'; return parseInt(p[2],10) + ' ' + MON[parseInt(p[1],10)-1] + ' ' + p[0]; };
  const monthLong = (ym) => { const p = String(ym || '').split('-'); if (p.length < 2) return ''; return MONL[parseInt(p[1],10)-1] + ' ' + p[0]; };
  const netDays = (() => { try { if (!invoice.generatedDate || !invoice.dueDate) return 14; const d = Math.round((new Date(invoice.dueDate+'T00:00:00') - new Date(invoice.generatedDate+'T00:00:00')) / 86400000); return d > 0 ? d : 14; } catch (e) { return 14; } })();
  const periodMain = invoice.monthYear ? monthLong(invoice.monthYear) : '';
  const periodSub = client.billingType === 'commission' ? 'Commission' : 'Retainer';

  // ── line items (current month + any prior-outstanding reconciliation rows) ──
  const serviceDesc = client.billingType === 'commission'
    ? `Marketing services — ${esc(client.commissionRate)}% commission on sales of ${fmtAmt(invoice.salesFigure || 0, curr)}`
    : 'Monthly marketing retainer services';

  const recon = invoice.reconciliation;
  const rows = [];
  rows.push({ desc: serviceDesc, amt: invoice.amount });
  if (recon && recon.priorInvoices && recon.priorInvoices.length) {
    recon.priorInvoices.forEach(pi => rows.push({
      desc: `Outstanding — ${esc(pi.invoiceNumber || '')}${pi.monthYear ? ` (${esc(pi.monthYear)})` : ''}`,
      amt: pi.amount, muted: true,
    }));
  }
  const totalDue = recon ? recon.cumulativeTotal : invoice.amount;

  const itemRows = rows.map(r => `
        <tr>
          <td class="c-desc${r.muted ? ' muted' : ''}">${r.desc}</td>
          <td class="c-qty">${r.muted ? '' : '1'}</td>
          <td class="c-amt">${esc(fmtAmt(r.amt, curr))}</td>
        </tr>`).join('');

  const payRows = curr !== 'PKR'
    ? [
        ['Account Title', BANK_INTL.title],
        ['Bank', BANK_INTL.bank],
        ['Branch Code', BANK_INTL.branchCode],
        ['Account Number', BANK_INTL.account],
        ['IBAN', BANK_INTL.iban],
        ['SWIFT', BANK_INTL.swift],
      ]
    : [
        ['Account Name', BANK.name],
        ['Bank', BANK.bank],
        ['Account Number', BANK.account],
        ['IBAN', BANK.iban],
      ];
  const pay = payRows.map(([l, v]) => `<div class="payrow"><span class="pl">${esc(l)}</span><span class="pv">${esc(v)}</span></div>`).join('');

  return `<!doctype html><html lang="en"><head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>Invoice ${esc(invoice.invoiceNumber || '')} · Strategists</title>
<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;1,400&family=Bodoni+Moda:opsz,wght@6..96,400;6..96,500&family=Inter+Tight:wght@300;400;500&family=JetBrains+Mono:wght@400;500&display=swap" rel="stylesheet">
<style>
  *{box-sizing:border-box}
  html,body{margin:0}
  body{background:${INK};color:${TXT};font-family:'Inter Tight',system-ui,sans-serif;font-weight:300;-webkit-font-smoothing:antialiased}
  .serif{font-family:'Playfair Display',Georgia,serif;font-weight:400}
  .mono{font-family:'JetBrains Mono',ui-monospace,monospace}
  .lbl{font-family:'JetBrains Mono',ui-monospace,monospace;font-size:9px;font-weight:400;letter-spacing:.22em;text-transform:uppercase;color:${FAINT}}
  .sheet{max-width:860px;margin:0 auto;padding:76px 74px 60px}
  /* Header */
  .head{display:flex;justify-content:space-between;align-items:flex-start;gap:24px;padding-bottom:30px;border-bottom:1px solid ${LINE}}
  .brand{font-family:'Bodoni Moda','Didot',Georgia,serif;font-weight:400;font-size:46px;letter-spacing:.24em;text-indent:.24em;color:${TXT};line-height:1}
  .head-r{text-align:right;display:flex;flex-direction:column;gap:7px;align-items:flex-end}
  .head-r .mono{font-size:10px;letter-spacing:.18em;text-transform:uppercase;color:${MUT}}
  .pill{margin-top:6px;padding:4px 11px;border-radius:2px;font-family:'JetBrains Mono',ui-monospace,monospace;font-size:8.5px;font-weight:400;letter-spacing:.18em;border:1px solid currentColor}
  /* Parties */
  .parties{display:flex;justify-content:space-between;gap:40px;margin-top:58px}
  .p-name{font-family:'Playfair Display',Georgia,serif;font-weight:400;font-size:33px;color:${TXT};margin-top:16px;line-height:1.05}
  .p-sub{font-size:15px;font-weight:300;color:${MUT};line-height:1.65;margin-top:16px}
  .period{text-align:right}
  .period .p-name{font-size:30px}
  /* Items */
  table{width:100%;border-collapse:collapse;margin-top:66px}
  thead th{padding:0 0 15px;text-align:left;border-bottom:1px solid ${LINE};font-family:'JetBrains Mono',ui-monospace,monospace;font-size:9px;font-weight:400;letter-spacing:.2em;text-transform:uppercase;color:${FAINT}}
  thead th.c-qty,thead th.c-amt{text-align:right}
  tbody td{padding:22px 0;border-bottom:1px solid ${HAIR};font-size:15px;font-weight:300;vertical-align:top}
  .c-desc{color:${TXT}} .c-desc.muted{color:${MUT}}
  .c-qty{font-family:'JetBrains Mono',ui-monospace,monospace;text-align:right;width:90px;color:${MUT};font-variant-numeric:lining-nums tabular-nums}
  .c-amt{font-family:'JetBrains Mono',ui-monospace,monospace;text-align:right;width:180px;color:${TXT};font-variant-numeric:lining-nums tabular-nums;white-space:nowrap}
  /* Total */
  .total{margin-top:40px;text-align:right}
  .total .lbl{margin-bottom:16px}
  .total .amt{font-family:'Playfair Display',Georgia,serif;font-weight:400;font-size:64px;line-height:1;color:${TXT};font-variant-numeric:lining-nums tabular-nums;white-space:nowrap}
  /* Payment */
  .pay{margin-top:70px;max-width:520px}
  .payrow{display:flex;gap:18px;padding:7px 0}
  .payrow .pl{width:140px;flex-shrink:0;font-family:'JetBrains Mono',ui-monospace,monospace;font-size:9px;letter-spacing:.16em;text-transform:uppercase;color:${FAINT};padding-top:2px}
  .payrow .pv{font-family:'JetBrains Mono',ui-monospace,monospace;font-size:12.5px;color:${TXT};font-variant-numeric:lining-nums tabular-nums;word-break:break-word}
  /* Footer */
  .foot{margin-top:80px;padding-top:24px;border-top:1px solid ${LINE};display:flex;justify-content:space-between;gap:20px;align-items:flex-end}
  .foot .mono{font-family:'JetBrains Mono',ui-monospace,monospace;font-size:9.5px;letter-spacing:.16em;text-transform:uppercase;color:${FAINT};line-height:1.9}
  .foot .r{text-align:right}
  /* Download button */
  #pdfbtn{position:fixed;bottom:20px;right:20px;z-index:40;background:#A98953;color:${INK};border:1px solid #A98953;border-radius:2px;padding:12px 20px;font-family:'JetBrains Mono',ui-monospace,monospace;font-size:10px;font-weight:400;letter-spacing:.14em;text-transform:uppercase;cursor:pointer;box-shadow:0 10px 30px rgba(0,0,0,.5)}
  @media print{
    html,body{margin:0;background:${INK};-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .sheet{max-width:none;width:100%;min-height:100vh}
    #pdfbtn{display:none!important}
    @page{margin:0}
  }
</style></head>
<body>
  <div class="sheet">
    <div class="head">
      <div class="brand">STR</div>
      <div class="head-r">
        <div class="mono">Invoice ${esc(invoice.invoiceNumber || '—')}</div>
        <div class="mono">Issued ${esc(longDate(invoice.generatedDate))}</div>
        <div class="mono">Due ${esc(longDate(invoice.dueDate))}</div>
        <span class="pill" style="color:${st.color}">${st.label}</span>
      </div>
    </div>

    <div class="parties">
      <div class="billed">
        <div class="lbl">Billed To</div>
        <div class="p-name">${esc(client.name || '—')}</div>
        <div class="p-sub">${client.email ? esc(client.email) + '<br/>' : ''}${esc(client.city || 'Pakistan')}</div>
      </div>
      ${periodMain ? `<div class="period">
        <div class="lbl">Period</div>
        <div class="p-name">${esc(periodMain)}</div>
        <div class="p-sub">${esc(periodSub)}</div>
      </div>` : ''}
    </div>

    <table>
      <thead><tr><th class="c-desc">Description</th><th class="c-qty">Qty</th><th class="c-amt">Amount</th></tr></thead>
      <tbody>${itemRows}</tbody>
    </table>

    <div class="total">
      <div class="lbl">${recon ? 'Cumulative Total Due' : 'Total Due'}</div>
      <div class="amt">${esc(fmtAmt(totalDue, curr))}</div>
    </div>

    <div class="pay">
      <div class="lbl" style="margin-bottom:16px">Payment</div>
      ${pay}
    </div>

    <div class="foot">
      <div class="mono">Payable by wire or Wise</div>
      <div class="mono r">strategistspk.com<br/>Net ${netDays} days</div>
    </div>
  </div>

  <button id="pdfbtn" onclick="downloadPdf()">↓ Download PDF</button>
  <script>
  // Size the PDF page to the invoice itself (content-sized, zero margin).
  function downloadPdf(){
    var sheet=document.querySelector('.sheet');
    var W=860, prevW=sheet.style.width, prevMax=sheet.style.maxWidth;
    sheet.style.maxWidth='none'; sheet.style.width=W+'px';
    function go(){
      var H=Math.ceil(sheet.getBoundingClientRect().height);
      var st=document.getElementById('pageStyle')||document.createElement('style');
      st.id='pageStyle'; st.textContent='@page{size:'+W+'px '+H+'px;margin:0}';
      document.head.appendChild(st);
      setTimeout(function(){ window.print(); sheet.style.width=prevW; sheet.style.maxWidth=prevMax; }, 60);
    }
    if(document.fonts && document.fonts.ready){ document.fonts.ready.then(function(){ setTimeout(go,40); }); } else { go(); }
  }
  if(location.search.indexOf('print')>=0){ window.addEventListener('load',function(){ setTimeout(downloadPdf,600); }); }
  </script>
</body></html>`;
}

// Open the branded invoice in a new tab, ready to view / save as PDF.
export function openInvoiceDocument(client, invoice) {
  const html = buildInvoiceHtml(client || {}, invoice || {});
  const w = window.open('', '_blank');
  if (!w) return false;               // popup blocked
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}
